import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { ShoppingCart, Truck, Package, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/LanguageSelector';
import FiscalFields from '@/components/FiscalFields';
import LegalDocDialog from '@/components/LegalDocDialog';
import InstallAppButton, { InstallBanner } from '@/components/InstallAppButton';
import DemoEntry from '@/components/DemoEntry';
import { AUTH } from '@/constants/testIds';
import { usePaymentsConfig } from '@/lib/usePaymentsConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The two categories shown at registration map to the backend roles:
//   comprador -> "restaurant"  (bar, cafetería, restaurante, tienda, etc.)
//   vendedor  -> "supplier"    (distribuidor, proveedor, fabricante, etc.)
const CATEGORIES = [
  {
    id: 'restaurant',
    label: 'reg.buyer',
    sub: 'reg.buyerSub',
    icon: ShoppingCart,
    testId: AUTH.roleRestaurantButton,
    perk: 'reg.buyerPerk'
  },
  {
    id: 'supplier',
    label: 'reg.seller',
    sub: 'reg.sellerSub',
    icon: Truck,
    testId: AUTH.roleSupplierButton,
    perk: 'reg.sellerPerk'
  }
];

const LoginRegister = () => {
  const { login, register } = useAuth();
  const paymentsCfg = usePaymentsConfig();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(() => {
    const flagged = sessionStorage.getItem('rs_open_register') === '1';
    sessionStorage.removeItem('rs_open_register');
    return !flagged && !new URLSearchParams(window.location.search).has('register');
  });
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [showForgot, setShowForgot] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    business_name: '',
    cif: '',
    phone: '',
    postal_code: '',
    work_radius_km: 50,
    country: 'ES',
    region: '',
    tax_country: 'ES',
    vat_number: '',
    currency: 'EUR'
  });
  const { t, i18n } = useTranslation();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success(t('reg.welcome'));
      } else {
        if (!selectedRole) {
          toast.error(t('reg.selectRole'));
          setLoading(false);
          return;
        }
        if (!acceptTerms || !acceptPrivacy) {
          toast.error(t('terms.required'));
          setLoading(false);
          return;
        }
        if (formData.country === 'ES' && !/^\d{5}$/.test(formData.postal_code)) {
          toast.error(t('reg.invalidPostal'));
          setLoading(false);
          return;
        }
        await register({ ...formData, accept_terms: true, vat_number: formData.vat_number || null, region: formData.region || null, language: i18n.language?.slice(0, 2) || 'es', role: selectedRole, work_radius_km: parseInt(formData.work_radius_km) });
        toast.success(t('reg.created'));
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('reg.authError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 pb-32">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8 fade-in-up">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-emerald-600 p-3 rounded-lg">
              <Package size={32} className="text-white" />
            </div>
          </div>
          <div className="flex justify-end mb-2"><LanguageSelector compact={false} /></div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-2">
            RestoSupply
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            {t('reg.tagline')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm fade-in-up-delay-1" data-testid={AUTH.loginForm}>
          {/* Google Auth (recommended) */}
          <button
            type="button"
            data-testid="google-auth-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + '/auth/callback';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full mb-4 flex items-center justify-center gap-3 border border-slate-300 rounded-lg py-3 text-sm font-medium text-slate-800 hover:bg-slate-50 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            {t('auth.google')}
          </button>
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-2 text-slate-400">{t('reg.orEmail')}</span>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                {/* Category Selection: only 2 options */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-slate-900">
                    {t('reg.roleQ')} *
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {CATEGORIES.map((r) => {
                      const Icon = r.icon;
                      const active = selectedRole === r.id;
                      return (
                        <button
                          key={r.id}
                          type="button"
                          data-testid={r.testId}
                          onClick={() => setSelectedRole(r.id)}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 text-left ${
                            active ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <Icon size={22} className={active ? 'text-emerald-600' : 'text-slate-400'} />
                          <p className="text-sm font-medium text-slate-900 mt-2">{t(r.label)}</p>
                          <p className="text-xs text-slate-500 leading-snug">{t(r.sub)}</p>
                        </button>
                      );
                    })}
                  </div>
                  {selectedRole && (
                    <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded p-2" data-testid="register-role-perk">
                      {selectedRole === 'supplier' && !paymentsCfg.payments_enabled ? t('reg.sellerPerkBeta') : t(CATEGORIES.find(c => c.id === selectedRole)?.perk)}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_name" className="text-sm font-medium text-slate-900">
                    {t('auth.businessName')} *
                  </Label>
                  <Input
                    id="business_name"
                    data-testid={AUTH.businessNameInput}
                    type="text"
                    required={!isLogin}
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="rounded-lg"
                    placeholder={t('reg.bizPlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cif" className="text-sm font-medium text-slate-900">
                    {t('auth.cif')} *
                  </Label>
                  <Input
                    id="cif"
                    data-testid={AUTH.cifInput}
                    type="text"
                    required={!isLogin}
                    value={formData.cif}
                    onChange={(e) => setFormData({ ...formData, cif: e.target.value })}
                    className="rounded-lg"
                    placeholder="A12345678"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-900">
                    {t('auth.phone')} *
                  </Label>
                  <Input
                    id="phone"
                    data-testid={AUTH.phoneInput}
                    type="tel"
                    required={!isLogin}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="rounded-lg"
                    placeholder="+34 600 000 000"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="postal_code" className="text-sm font-medium text-slate-900">
                      {t('auth.postalCode')} *
                    </Label>
                    <Input
                      id="postal_code"
                      data-testid="postal-code-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={5}
                      required={!isLogin}
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.replace(/\D/g, '') })}
                      className="rounded-lg"
                      placeholder="08001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="work_radius_km" className="text-sm font-medium text-slate-900">
                      {t('reg.radiusShort')} *
                    </Label>
                    <Input
                      id="work_radius_km"
                      data-testid="work-radius-input"
                      type="number"
                      min={1}
                      max={1000}
                      required={!isLogin}
                      value={formData.work_radius_km}
                      onChange={(e) => setFormData({ ...formData, work_radius_km: e.target.value })}
                      className="rounded-lg"
                      placeholder="50"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 -mt-2">
                  {t('reg.radiusHint')}
                </p>
                <FiscalFields idPrefix="register" value={formData} onChange={(partial) => setFormData((f) => ({ ...f, ...partial }))} />
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="legal-acceptance">
                  <label className="flex items-start gap-2 text-xs text-slate-700">
                    <input type="checkbox" data-testid="accept-terms-checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="mt-0.5" />
                    <span>{t('terms.acceptTerms')} <button type="button" data-testid="open-terms-doc" onClick={() => setLegalDoc('terms')} className="text-emerald-700 underline font-medium">{t('terms.termsLink')}</button> *</span>
                  </label>
                  <label className="flex items-start gap-2 text-xs text-slate-700">
                    <input type="checkbox" data-testid="accept-privacy-checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} className="mt-0.5" />
                    <span>{t('terms.acceptPrivacy')} <button type="button" data-testid="open-privacy-doc" onClick={() => setLegalDoc('privacy')} className="text-emerald-700 underline font-medium">{t('terms.privacyLink')}</button> *</span>
                  </label>
                </div>
                <LegalDocDialog
                  docKey={legalDoc}
                  open={!!legalDoc}
                  onOpenChange={(o) => { if (!o) setLegalDoc(null); }}
                  onAccept={() => { if (legalDoc === 'terms') setAcceptTerms(true); else setAcceptPrivacy(true); }}
                />
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-900">
                {t('auth.email')} *
              </Label>
              <Input
                id="email"
                data-testid={AUTH.emailInput}
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="rounded-lg"
                placeholder="tu@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-900">
                {t('auth.password')} *
              </Label>
              <Input
                id="password"
                data-testid={AUTH.passwordInput}
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="rounded-lg"
                placeholder="••••••••"
              />
              {isLogin && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    data-testid="forgot-password-link"
                    className="text-xs text-emerald-700 hover:text-emerald-800 hover:underline"
                  >
                    {t('auth.forgot')}
                  </button>
                </div>
              )}
            </div>

            <Button
              type="submit"
              data-testid={isLogin ? AUTH.loginButton : AUTH.registerButton}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-6 text-base font-medium transition-transform hover:-translate-y-px shadow-sm hover:shadow-md"
            >
              {loading ? t('auth.loading') : isLogin ? t('auth.login') : t('auth.register')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-slate-600 hover:text-emerald-600 transition-colors"
            >
              {isLogin ? t('reg.noAccount') : t('reg.hasAccount')}
            </button>
          </div>
          <div className="mt-4 flex justify-center">
            <InstallAppButton className="h-9 text-sm" />
          </div>
        </div>

        {isLogin && <DemoEntry />}
        <p className="mt-6 text-center text-xs text-slate-400">
          <a href="/privacidad" className="hover:text-slate-600">{t('terms.privacyLink')}</a> · <a href="/condiciones" className="hover:text-slate-600">{t('terms.termsLink')}</a> · <a href="/eliminar-cuenta" className="hover:text-slate-600" data-testid="login-delete-account-link">{t('delAcc.link')}</a>
        </p>
      </div>

      <InstallBanner />
      <ForgotPasswordDialog open={showForgot} onOpenChange={setShowForgot} />
    </div>
  );
};

const ForgotPasswordDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setEmail(''); setPhone(''); setNewPassword(''); setConfirmPassword('');
  };

  const handleSubmit = async () => {
    if (newPassword.length < 6) {
      toast.error(t('reg.pwdShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('reg.pwdMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        phone,
        new_password: newPassword
      });
      toast.success(t('reg.pwdUpdated'));
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('reg.pwdFail'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="forgot-password-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={18} className="text-emerald-600" />
            {t('reg.forgotTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('reg.forgotDesc')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>{t('auth.email')} *</Label>
            <Input
              data-testid="forgot-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('auth.phone')} *</Label>
            <Input
              data-testid="forgot-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+34 600 000 000"
            />
          </div>
          <div className="space-y-2">
            <Label>{t('reg.newPassword')} *</Label>
            <Input
              data-testid="forgot-new-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('reg.minChars')}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('reg.repeatPassword')} *</Label>
            <Input
              data-testid="forgot-confirm-password-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('reg.repeatPassword')}
            />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="forgot-submit-button"
            onClick={handleSubmit}
            disabled={submitting || !email || !phone || !newPassword || !confirmPassword}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? t('reg.saving') : t('reg.resetPassword')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginRegister;
